import React from "react";
import { Button, Card, CardBody, Col, Input, Row } from "reactstrap";

const DEFAULT_VISUAL_STYLE = {
  borderColor: "#e6ebf2",
  borderHoverColor: "#d8e1ec",
  bgColor: "#fbfcfe",
  hoverBgColor: "#ffffff",
  accentColor: "#5d6d7e",
  titleColor: "#1f2937",
};

const ExpandableDataCardList = ({
  data = [],
  columns = [],
  detailColumns = [],
  gridTemplateColumns,
  emptyMessage = "Chưa có dữ liệu...",
  allSelected = false,
  onToggleSelectAll,
  selectedIds = [],
  onToggleItem,
  expandedIds = [],
  onToggleExpand,
  getItemId = (item) => item.id,
  getVisualStyle = () => DEFAULT_VISUAL_STYLE,
  renderTitle,
  renderMainValue,
  renderDetailValue,
  renderExpandedContent,
  renderDetailToggle,
  renderActions,
  getItemHasDetails,
  selectable = true,
  titleHeaderLabel = "Sản phẩm",
  detailActionHeaderLabel = "Chi tiết",
  actionHeaderLabel = "Khác",
  headerPaddingLeft = 16,
  headerPaddingRight = 17,
  highlightedId = null,
  highlightedIds = [],
  getCardStyle,
  getCardClassName,
}) => {
  const [hoveredId, setHoveredId] = React.useState(null);
  const hasRows = data.length > 0;
  const hasAnyDetails = detailColumns.length > 0 || Boolean(renderExpandedContent);
  const hasAnyItemDetails = React.useMemo(() => {
    if (getItemHasDetails) {
      return data.some((item) => Boolean(getItemHasDetails(item)));
    }
    return hasAnyDetails;
  }, [data, getItemHasDetails, hasAnyDetails]);
  const showDetailColumn = Boolean(renderDetailToggle) && hasAnyItemDetails;
  const selectionColumnTemplate = selectable ? "40px " : "";

  const resolvedGridTemplate = React.useMemo(() => {
    if (typeof gridTemplateColumns === "function") {
      return gridTemplateColumns(showDetailColumn);
    }
    if (gridTemplateColumns) {
      return gridTemplateColumns;
    }
    return `${selectionColumnTemplate}minmax(220px, 1fr) ${columns
      .map((column) => `minmax(${column.width || 100}px, auto)`)
      .join(" ")} ${showDetailColumn ? "96px " : ""}72px`;
  }, [gridTemplateColumns, showDetailColumn, selectionColumnTemplate, columns]);

  if (!hasRows) {
    return <div className="text-center text-muted py-4 border rounded tour-list-products">{emptyMessage}</div>;
  }

  return (
    <div className="tour-list-products">
      <div
        className="d-none d-xl-block text-muted text-small font-weight-semibold pb-2 expandable-data-card-header"
        style={{
          paddingLeft: headerPaddingLeft,
          paddingRight: headerPaddingRight,
        }}
      >
        <div
          className="align-items-center"
          style={{
            display: "grid",
            gridTemplateColumns: resolvedGridTemplate,
            columnGap: 10,
            minWidth: 0,
          }}
        >
          {selectable && (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minWidth: 0 }}
            >
              <Input
                type="checkbox"
                className="m-0"
                style={{ position: "static" }}
                checked={allSelected}
                onChange={onToggleSelectAll}
              />
            </div>
          )}
          <div className="text-left" style={{ minWidth: 0 }}>
            {titleHeaderLabel}
          </div>
          {columns.map((column) => (
            <div
              key={`card-header-${column.key}`}
              className={column.headerClass || column.cardValueClass || "text-center"}
              style={{
                minWidth: 0,
                maxWidth: column.maxWidth || "100%",
                overflow: column.overflow || "hidden",
                textOverflow: column.overflow === "visible" ? "clip" : "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {column.label}
            </div>
          ))}
          {showDetailColumn && (
            <div className="text-center" style={{ minWidth: 0 }}>
              {detailActionHeaderLabel}
            </div>
          )}
          <div className="text-center" style={{ minWidth: 0 }}>
            {actionHeaderLabel}
          </div>
        </div>
      </div>

      {data.map((item) => {
        const itemId = getItemId(item);
        const isHovered = hoveredId === itemId;
        const isExpanded = expandedIds.includes(itemId);
        const isHighlighted =
          (highlightedId !== null &&
            highlightedId !== undefined &&
            String(highlightedId) === String(itemId)) ||
          (Array.isArray(highlightedIds) &&
            highlightedIds.some((id) => String(id) === String(itemId)));
        const visualStyle = {
          ...DEFAULT_VISUAL_STYLE,
          ...(getVisualStyle(item) || {}),
        };
        const hasDetails = getItemHasDetails
          ? Boolean(getItemHasDetails(item))
          : hasAnyDetails;
        const extraCardStyle = getCardStyle
          ? getCardStyle(item, { isHovered, isHighlighted, isExpanded })
          : null;
        const extraCardClassName = getCardClassName
          ? getCardClassName(item, { isHovered, isHighlighted, isExpanded })
          : "";
        const cardOpacity =
          visualStyle.opacity !== undefined
            ? isHovered
              ? Math.min(1, Number(visualStyle.opacity) + 0.25)
              : visualStyle.opacity
            : 1;

        return (
          <Card
            key={itemId}
            id={`product-card-${itemId}`}
            className={`mb-2 border-0 expandable-data-card ${isHighlighted ? "expandable-card-highlighted" : ""} ${extraCardClassName || ""}`.trim()}
            onMouseEnter={() => setHoveredId(itemId)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              position: "relative",
              zIndex: isHighlighted ? 3 : isHovered ? 2 : 1,
              borderRadius: 16,
              backgroundColor: isHighlighted
                ? "#f0f7ff"
                : isHovered
                  ? visualStyle.hoverBgColor
                  : visualStyle.bgColor,
              border: isHighlighted
                ? "2px solid #2563eb"
                : `1px solid ${
                    isHovered ? visualStyle.borderHoverColor : visualStyle.borderColor
                  }`,
              borderLeft: isHighlighted
                ? "6px solid #2563eb"
                : `4px solid ${visualStyle.accentColor}`,
              boxShadow: isHighlighted
                ? "0 0 0 3px rgba(37, 99, 235, 0.25), 0 12px 28px rgba(37, 99, 235, 0.16)"
                : isHovered
                  ? "0 10px 24px rgba(15, 23, 42, 0.1)"
                  : "0 1px 0 rgba(0, 0, 0, 0.04)",
              transform: isHighlighted ? "translateY(-1px)" : "none",
              transition:
                "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease",
              overflow: "visible",
              opacity: cardOpacity,
              filter: visualStyle.filter || undefined,
              ...(extraCardStyle || {}),
            }}
          >
            <CardBody className="py-2 px-3 expandable-data-card-body">
              <div
                className="align-items-center expandable-data-card-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: resolvedGridTemplate,
                  columnGap: 10,
                  minWidth: 0,
                }}
              >
                {selectable && (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ minWidth: 0 }}
                  >
                    <Input
                      type="checkbox"
                      className="m-0"
                      style={{ position: "static" }}
                      checked={selectedIds.includes(itemId)}
                      onChange={(event) =>
                        onToggleItem && onToggleItem(itemId, event)
                      }
                    />
                  </div>
                )}

                <div
                  className="d-flex align-items-center expandable-data-card-title-cell"
                  style={{ minWidth: 0, whiteSpace: "normal" }}
                >
                  {renderTitle ? (
                    renderTitle({ item, isHovered, visualStyle, isHighlighted })
                  ) : (
                    <div
                      className="font-weight-semibold"
                      style={{
                        color: visualStyle.titleColor,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.name}
                    </div>
                  )}
                </div>

                {columns.map((column) => (
                  <div
                    key={`card-main-${itemId}-${column.key}`}
                    className={`expandable-data-card-value ${
                      column.cardValueClass || "text-center"
                    }`}
                    style={{
                      minWidth: 0,
                      maxWidth: column.maxWidth || "100%",
                      overflow: column.overflow || "hidden",
                      textOverflow: column.overflow === "visible" ? "clip" : "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderMainValue
                      ? renderMainValue({
                          item,
                          column,
                          visualStyle,
                          isHovered,
                          isHighlighted,
                        })
                      : item[column.key]}
                  </div>
                ))}

                {showDetailColumn && (
                  <div
                    className="d-flex align-items-center justify-content-center expandable-data-card-detail-cell"
                    style={{
                      minWidth: 0,
                      alignSelf: "stretch",
                    }}
                  >
                    {renderDetailToggle({
                      item,
                      itemId,
                      isExpanded,
                      hasDetails,
                      visualStyle,
                      isHovered,
                      toggleExpand: () => onToggleExpand && onToggleExpand(itemId),
                    })}
                  </div>
                )}

                <div
                  className="d-flex align-items-center justify-content-center expandable-data-card-action-cell"
                  style={{
                    minWidth: 0,
                    position: "static",
                    alignSelf: "stretch",
                    backgroundColor: isHovered
                      ? visualStyle.hoverBgColor
                      : visualStyle.bgColor,
                  }}
                >
                  {renderActions ? (
                    renderActions({
                      item,
                      itemId,
                      isExpanded,
                      hasDetails,
                      visualStyle,
                      isHovered,
                      toggleExpand: () => onToggleExpand && onToggleExpand(itemId),
                    })
                  ) : (
                    hasDetails && (
                      <Button
                        color="link"
                        className="p-0 text-muted d-inline-flex align-items-center justify-content-center"
                        style={{
                          textDecoration: "none",
                          boxShadow: "none",
                          width: 22,
                          height: 22,
                        }}
                        onClick={() => onToggleExpand && onToggleExpand(itemId)}
                      >
                        <span style={{ fontSize: "1rem", lineHeight: 1 }}>
                          {isExpanded ? "▴" : "▾"}
                        </span>
                      </Button>
                    )
                  )}
                </div>
              </div>

              {isExpanded && hasDetails && (
                <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #dce3ee" }}>
                  {renderExpandedContent ? (
                    renderExpandedContent({ item, itemId, visualStyle, isHovered })
                  ) : (
                    <Row className="mx-0">
                      {detailColumns.map((column) => (
                        <Col
                          xs="12"
                          sm="6"
                          md="3"
                          className="px-2 py-1"
                          key={`card-extra-${itemId}-${column.key}`}
                        >
                          <small className="text-muted d-block">{column.label}</small>
                          <div
                            className={`font-weight-semibold text-left ${
                              column.cardValueClass || ""
                            }`}
                          >
                            {renderDetailValue
                              ? renderDetailValue({ item, column, visualStyle })
                              : item[column.key]}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

export default ExpandableDataCardList;
